import React, { useState, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'

import {
  List,
  Button,
  Modal,
  message,
  Tooltip,
  Tag,
  Input,
  Icon,
  Empty,
  Dropdown,
  Menu,
  Popover
} from 'antd'
import {
  getAiVideos,
  deleteAiVideo,
  getAiVideoById
} from '../../services/api/aiVideoGeneration'
import './VideoList.scss'
import NewHeader from '../../components/NewHeader'
import AIVideoModal from './components/VideoGeneration/components/FinalVideo'
import StatusView from './components/StatusView/StatusView'
import moment from 'moment'
import VideoDuration from './components/VideoDuration'
import VideoAnalytics from './components/VideoAnalytics/VideoAnalytics'
import { ImagePreview } from '../../components'
import StoryboardDownloadModal from './components/StoryboardDownloadModal'
import { generateStoryboardPdf } from './utils/storyboardPdf'

const AI_VIDEO_STEPS = [
  { key: 'uploadDocument', title: 'Upload', icon: 'file-text' },
  { key: 'voiceover', title: 'Voiceover', icon: 'sound' },
  { key: 'videoStyle', title: 'Visual Setup', icon: 'camera' },
  { key: 'shotList', title: 'Shot List', icon: 'ordered-list' },
  { key: 'imageGeneration', title: 'Images', icon: 'picture' },
  { key: 'videoGeneration', title: 'Video', icon: 'video-camera' },
  { key: 'finalVideo', title: 'Final Result', icon: 'play-circle' }
]

const resolveVoiceoverStepByAudioMode = item => {
  const mode = item?.audio_mode || item?.audioMode || 'narration'

  if (mode === 'dialogue') {
    return 'characterVoiceover'
  }

  return 'narrationVoiceover'
}

const resolveStepKey = (item, stepKey) => {
  if (stepKey === 'voiceover') {
    return resolveVoiceoverStepByAudioMode(item)
  }
  return stepKey
}

const VideoList = ({ onCreateNew, onEditVideo }) => {
  const userInfo = useSelector(state => state.user.info)
  const isSuperAdmin = userInfo?.isSuperAdmin || userInfo?.is_super_admin

  const [videos, setVideos] = useState([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [offset, setOffset] = useState(0)
  const [total, setTotal] = useState(0)
  const PAGE_SIZE = 12

  const [searchText, setSearchText] = useState('')
  const [selectedVideo, setSelectedVideo] = useState(null)
  const [previewModalVisible, setPreviewModalVisible] = useState(false)
  const [analyticsModalVisible, setAnalyticsModalVisible] = useState(false)
  const [analyzingVideo, setAnalyzingVideo] = useState(null)

  const [creatorModalVisible, setCreatorModalVisible] = useState(false)
  const [selectedCreator, setSelectedCreator] = useState(null)
  const [pdfModalVisible, setPdfModalVisible] = useState(false)
  const [pdfPreparing, setPdfPreparing] = useState(false)
  const [pdfProgress, setPdfProgress] = useState(0)
  const [pdfStatusText, setPdfStatusText] = useState('')
  const [pendingPdfItem, setPendingPdfItem] = useState(null)
  const [pdfQuality, setPdfQuality] = useState('medium')

  const handleCreatorClick = (e, creator) => {
    e.stopPropagation()
    if (creator) {
      setSelectedCreator(creator)
      setCreatorModalVisible(true)
    }
  }

  // Initial fetch or search change
  useEffect(() => {
    setVideos([])
    setOffset(0)
    setHasMore(true)
    fetchVideos(0, true)
  }, [searchText])

  const fetchVideos = async (currentOffset, isReset = false) => {
    setLoading(true)
    try {
      const response = await getAiVideos(currentOffset, PAGE_SIZE)

      if (response.data) {
        const videosFetched = response.data.videos || response.data || []

        setVideos(prev =>
          isReset ? videosFetched : [...prev, ...videosFetched]
        )

        // Update total if provided
        if (response.data.total !== undefined) {
          setTotal(response.data.total)
        }

        // Determine if there's more based on response length
        const moreAvailable = videosFetched.length === PAGE_SIZE
        setHasMore(moreAvailable)

        if (!isReset) {
          setOffset(currentOffset)
        }
      }
    } catch (error) {
      console.error('Failed to fetch videos:', error)
      message.error('Failed to load videos')
    } finally {
      setLoading(false)
    }
  }

  const handleLoadMore = () => {
    const nextOffset = offset + PAGE_SIZE
    fetchVideos(nextOffset)
  }

  const handleDelete = async videoId => {
    try {
      await deleteAiVideo(videoId)
      message.success('Video deleted successfully')
      // Refresh the current view
      setVideos([])
      setOffset(0)
      fetchVideos(0, true)
    } catch (error) {
      console.error('Failed to delete video:', error)
      message.error('Failed to delete video')
    }
  }

  const handlePreview = videoId => {
    const video = videos.find(video => video.id === videoId)
    setSelectedVideo(video)
    setPreviewModalVisible(true)
  }

  const handleDownloadPdf = async item => {
    try {
      setPdfPreparing(true)
      await generateStoryboardPdf({
        item,
        fetchVideoById: getAiVideoById,
        quality: pdfQuality,
        onProgress: ({ status, progress }) => {
          if (status) setPdfStatusText(status)
          if (typeof progress === 'number') setPdfProgress(progress)
        }
      })
      setTimeout(() => {
        setPdfModalVisible(false)
        setPdfPreparing(false)
        setPendingPdfItem(null)
      }, 500)
    } catch (error) {
      console.error('Failed to generate storyboard PDF:', error)
      message.error('Failed to download PDF. Please try again.')
    } finally {
      setPdfPreparing(false)
    }
  }

  const filteredVideos = useMemo(
    () =>
      searchText
        ? videos.filter(video => {
            return (
              video.voiceover_text &&
              video.voiceover_text
                .toLowerCase()
                .includes(searchText.toLowerCase())
            )
          })
        : videos,
    [videos, searchText]
  )
  const isSearchActive = searchText.trim().length > 0
  const visibleVideoCount = filteredVideos.length
  const shouldShowLoadMore =
    hasMore && (!isSearchActive || visibleVideoCount >= PAGE_SIZE)

  const renderVideoRow = item => {
    const draftStatus = item.draft?.status
    const draftId = item.draft?.id

    const isReady = !!item.video_url
    const isProcessing =
      draftId &&
      !isReady &&
      (draftStatus === 'pending' || item.status === 'processing')
    const isFailed =
      !isReady && (draftStatus === 'failed' || item.status === 'failed')

    // Status mapping
    let statusLabel = draftId ? 'Draft' : 'Manual'
    let statusColor = 'default'
    if (isReady) {
      statusLabel = 'Ready'
      statusColor = 'blue'
    } else if (isProcessing) {
      statusLabel = 'Processing'
      statusColor = 'orange'
    } else if (isFailed) {
      statusLabel = 'Failed'
      statusColor = 'red'
    }

    return (
      <List.Item key={item.id} className="video-row-item">
        <div className={`video-row ${isFailed ? 'video-row--failed' : ''}`}>
          <div
            className="video-row__preview"
            onClick={() => isReady && handlePreview(item.id)}
          >
            {isReady ? (
              <div className="video-thumbnail-wrapper">
                <video
                  src={`${item.video_url}#t=0.1`}
                  preload="metadata"
                  className="video-thumbnail"
                  playsInline
                  muted
                />
                <div className="play-icon-overlay">
                  <Icon type="play-circle" theme="filled" />
                  <span className="duration-pill">
                    <VideoDuration
                      videoUrl={item.video_url}
                      initialDuration={item.video_duration}
                    />
                  </span>
                </div>
              </div>
            ) : (
              <div
                className={`processing-state ${
                  isFailed
                    ? 'failed-state'
                    : isProcessing
                    ? 'pending-state'
                    : 'draft-state'
                }`}
              >
                <Icon
                  type={
                    isFailed
                      ? 'close-circle'
                      : isProcessing
                      ? 'loading'
                      : 'edit'
                  }
                />
                <span>
                  {isFailed
                    ? 'Failed'
                    : isProcessing
                    ? 'Processing'
                    : draftId
                    ? 'Draft'
                    : 'Unfinished'}
                </span>
              </div>
            )}
          </div>

          <div className="video-row__content">
            <div className="video-row__header">
              <Popover
                title={item.voiceover_text}
                placement="topLeft"
                trigger="click"
              >
                <div className="video-row__title-container">
                  <h4 className="video-row__title">
                    {item.voiceover_text?.length > 100
                      ? item.voiceover_text?.slice(0, 100) + '...'
                      : item.voiceover_text || 'Untitled Video'}
                  </h4>
                </div>
              </Popover>
            </div>

            <div className="video-row__meta">
              <div className="video-row__author">
                <div className="author-avatar" style={{ overflow: 'hidden' }}>
                  {item.creator?.avatar?.thumbnail ||
                  item.creator?.avatar?.link ? (
                    <img
                      src={
                        item.creator.avatar.thumbnail ||
                        item.creator.avatar.link
                      }
                      alt="creator"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <Icon type="user" />
                  )}
                </div>
                {item.creator ? (
                  <span
                    className="creator-link"
                    onClick={e => handleCreatorClick(e, item.creator)}
                  >
                    {item.creator.name ||
                      (item.creator.first_name && item.creator.last_name
                        ? `${item.creator.first_name} ${item.creator.last_name}`
                        : null) ||
                      item.creator.first_name ||
                      item.creator.email}
                  </span>
                ) : (
                  <span>AI Video</span>
                )}
                <span className="meta-dot">•</span>
                <span className="meta-date">
                  {moment(item.updated_at).fromNow()}
                </span>
              </div>
              <Tag
                color={statusColor}
                className={`video-row__status video-row__status--${statusLabel.toLowerCase()}`}
              >
                <div className="status-dot" />
                {statusLabel}
              </Tag>
            </div>
          </div>

          <div className="video-row__actions">
            <div className="action-buttons-group">
              <div className="action-slot">
                <Tooltip title="Edit Video">
                  <Button
                    className="video-row__action-btn"
                    icon="edit"
                    type="link"
                    onClick={e => {
                      e.stopPropagation()
                      onEditVideo(item)
                    }}
                  />
                </Tooltip>
              </div>

              {isReady ? (
                <div className="action-slot">
                  <Dropdown
                    overlay={
                      <Menu>
                        {AI_VIDEO_STEPS.map(step => (
                          <Menu.Item
                            key={step.key}
                            onClick={e => {
                              onEditVideo(item, resolveStepKey(item, step.key))
                            }}
                          >
                            <Icon type={step.icon} /> {step.title}
                          </Menu.Item>
                        ))}
                      </Menu>
                    }
                    placement="bottomRight"
                    trigger={['hover']}
                  >
                    <Tooltip title="Jump to Step">
                      <Button
                        className="video-row__action-btn"
                        icon="fast-forward"
                        type="link"
                        onClick={e => {
                          e.stopPropagation()
                          onEditVideo(item, 'finalVideo')
                        }}
                      />
                    </Tooltip>
                  </Dropdown>
                </div>
              ) : (
                <div className="action-slot">
                  <Tooltip title="Jump to Step (available when ready)">
                    <div
                      className="video-row__action-btn video-row__action-btn--disabled"
                      onClick={e => e.stopPropagation()}
                    >
                      <Icon type="fast-forward" />
                    </div>
                  </Tooltip>
                </div>
              )}

              <div className="action-slot">
                <Dropdown
                  trigger={['click']}
                  placement="bottomRight"
                  overlay={
                    <Menu
                      onClick={({ key, domEvent }) => {
                        domEvent.stopPropagation()
                        if (key === 'preview' && isReady) {
                          handlePreview(item.id)
                          return
                        }
                        if (key === 'analytics') {
                          setAnalyzingVideo(item)
                          setAnalyticsModalVisible(true)
                          return
                        }
                        if (key === 'pdf' && isReady) {
                          setPendingPdfItem(item)
                          setPdfProgress(0)
                          setPdfStatusText(
                            'This will prepare a storyboard PDF for this video.'
                          )
                          setPdfModalVisible(true)
                          return
                        }
                        if (key === 'delete') {
                          Modal.confirm({
                            title: 'Delete this video?',
                            content: 'This action cannot be undone.',
                            okText: 'Delete',
                            okType: 'danger',
                            cancelText: 'Cancel',
                            onOk: () => handleDelete(item.id)
                          })
                        }
                      }}
                    >
                      {isReady && (
                        <Menu.Item key="preview">
                          <Icon type="play-circle" /> Preview
                        </Menu.Item>
                      )}
                      {isReady && (
                        <Menu.Item key="pdf">
                          <Icon type="file-pdf" /> Download PDF
                        </Menu.Item>
                      )}
                      {isSuperAdmin && (
                        <Menu.Item key="analytics">
                          <Icon type="line-chart" /> Analytics
                        </Menu.Item>
                      )}
                      <Menu.Divider />
                      <Menu.Item key="delete" style={{ color: '#ff4d4f' }}>
                        <Icon type="delete" /> Delete
                      </Menu.Item>
                    </Menu>
                  }
                >
                  <Tooltip title="More Actions">
                    <Button
                      className="video-row__action-btn"
                      icon="ellipsis"
                      type="link"
                      onClick={e => e.stopPropagation()}
                    />
                  </Tooltip>
                </Dropdown>
              </div>
            </div>
          </div>
        </div>
      </List.Item>
    )
  }

  return (
    <div className="video-list-page">
      <div className="video-list-page__header">
        <div className="video-list-page__header-content">
          <div className="video-list-page__title-area">
            <h1>AI Video Studio</h1>
            <p>Create and manage your AI-generated video content</p>
          </div>
          <div className="video-list-page__actions">
            <NewHeader />
          </div>
        </div>

        <div className="video-list-page__filters">
          <Input
            prefix={<Icon type="search" style={{ color: 'rgba(0,0,0,.25)' }} />}
            placeholder="Search your videos..."
            className="video-list-search-input"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            allowClear
          />
          <Button
            type="primary"
            icon="plus"
            className="btn-create"
            onClick={onCreateNew}
          >
            Create New Video
          </Button>
        </div>
      </div>

      <div className="video-list-page__content">
        {loading && !videos.length ? (
          <StatusView
            title="Loading Videos"
            subTitle="Fetching your creative content..."
          />
        ) : (
          <div className="video-list-container">
            <List
              loading={loading && videos.length === 0}
              dataSource={filteredVideos}
              renderItem={renderVideoRow}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <div className="empty-state">
                        <h3>No videos found</h3>
                        <p>Get started by creating your first AI video</p>
                        <Button type="primary" onClick={onCreateNew}>
                          Create Video
                        </Button>
                      </div>
                    }
                  />
                )
              }}
            />
            {videos.length > 0 &&
              (!isSearchActive || visibleVideoCount > 0) && (
              <div className="video-list-footer">
                <div className="video-list-status">
                  Showing <strong>{visibleVideoCount}</strong>
                  {total > 0 && (
                    <span>
                      {' '}
                      of <strong>{total}</strong>
                    </span>
                  )}{' '}
                  videos
                </div>

                {shouldShowLoadMore && (
                  <div className="video-list-load-more">
                    <Button
                      loading={loading}
                      onClick={handleLoadMore}
                      className="btn-load-more"
                      icon={loading ? undefined : 'down'}
                    >
                      {loading
                        ? isSearchActive
                          ? 'Fetching more source videos...'
                          : `Fetching next ${PAGE_SIZE} videos...`
                        : isSearchActive
                        ? 'Load more source videos'
                        : `Load ${PAGE_SIZE} more videos`}
                    </Button>
                  </div>
                )}

                {!hasMore && (
                  <div className="video-list-end">
                    <Icon
                      type="check-circle"
                      theme="twoTone"
                      twoToneColor="#52c41a"
                    />
                    <span>You've explored all your cinematic creations</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {selectedVideo && selectedVideo.video_url && (
        <AIVideoModal
          visible={true}
          videoUrl={selectedVideo.video_url}
          title={'Your AI Generated Video'}
          onClose={() => setSelectedVideo({})}
          onEdit={step => onEditVideo(selectedVideo, step)}
        />
      )}

      <VideoAnalytics
        visible={analyticsModalVisible}
        onClose={() => {
          setAnalyticsModalVisible(false)
          setAnalyzingVideo(null)
        }}
        videoId={analyzingVideo?.id}
        userId={analyzingVideo?.creator_id}
        videoTitle={analyzingVideo?.voiceover_text}
      />

      <StoryboardDownloadModal
        visible={pdfModalVisible}
        preparing={pdfPreparing}
        progress={pdfProgress}
        statusText={pdfStatusText}
        quality={pdfQuality}
        canStart={!!pendingPdfItem}
        onQualityChange={value => setPdfQuality(value)}
        onCancel={() => {
          if (!pdfPreparing) {
            setPdfModalVisible(false)
            setPendingPdfItem(null)
          }
        }}
        onStart={() => {
          if (!pdfPreparing && pendingPdfItem) {
            handleDownloadPdf(pendingPdfItem)
          }
        }}
      />

      <Modal
        centered
        maskClosable={false}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                background: '#e6f7ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#1890ff',
                overflow: 'hidden'
              }}
            >
              {selectedCreator?.avatar?.thumbnail ||
              selectedCreator?.avatar?.link ? (
                <ImagePreview
                  src={
                    selectedCreator.avatar.thumbnail ||
                    selectedCreator.avatar.link
                  }
                  alt="creator profile"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <Icon type="user" />
              )}
            </div>
            <span style={{ fontSize: '16px', fontWeight: 500 }}>
              Creator Profile
            </span>
          </div>
        }
        visible={creatorModalVisible}
        centered
        width={400}
        onCancel={() => {
          setCreatorModalVisible(false)
          setSelectedCreator(null)
        }}
        footer={null}
        bodyStyle={{ paddingTop: 0 }}
      >
        {selectedCreator && (
          <div className="creator-details" style={{ padding: '8px 4px' }}>
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
            >
              <div>
                <div
                  style={{
                    fontSize: '12px',
                    color: '#8c8c8c',
                    marginBottom: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Full Name
                </div>
                <div
                  style={{
                    fontSize: '15px',
                    fontWeight: 500,
                    color: '#262626'
                  }}
                >
                  {selectedCreator.name ||
                    (selectedCreator.first_name && selectedCreator.last_name
                      ? `${selectedCreator.first_name} ${selectedCreator.last_name}`
                      : null) ||
                    selectedCreator.first_name ||
                    'N/A'}
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: '12px',
                    color: '#8c8c8c',
                    marginBottom: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Email Address
                </div>
                <div style={{ fontSize: '15px', color: '#262626' }}>
                  {selectedCreator.email || 'N/A'}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default VideoList
